/**
 * Region groups for Add Deployment drawer (Figma: accordion-based region selection).
 * Each group can be expanded to show radio options. Value is sent to deployV2 as
 * targetRegion (or gatewayRegion when value is "global").
 */
export interface RegionOption {
  key: string;
  value: string;
  /** Mirrors gatewayConfiguration.regionalGroup; unset for static fallback lists */
  regionalGroup?: string | null;
  /** From GET /v2/apis/regions; used for regional consumption hostname (e.g. us-e) */
  basePathIdentifier?: string | null;
}

/** Case-insensitive match of option key/value against API deployment region strings */
export function isRegionAlreadyDeployed(
  option: RegionOption,
  deployedRegions: string[] | undefined
): boolean {
  if (!deployedRegions?.length) return false;
  const norm = (s: string) => s.trim().toLowerCase();
  const keys = new Set([norm(option.key), norm(option.value)]);
  return deployedRegions.some((dep) => keys.has(norm(dep)));
}

/** Case-insensitive check for an existing global deployment entry. */
export function isGlobalAlreadyDeployed(
  deployedRegions: string[] | undefined
): boolean {
  if (!deployedRegions?.length) return false;
  return deployedRegions.some((dep) => dep.trim().toLowerCase() === "global");
}

const normKey = (s: string) => s.trim().toLowerCase();

/**
 * Normalized keys of regionalGroup values that already have a deployment (any provider),
 * using the full {@link RegionItemFromApi} catalog from GET /v2/apis/regions (unfiltered).
 */
export function buildOccupiedRegionalGroupKeys(
  deployedRegions: string[] | undefined,
  catalogItems: RegionItemFromApi[]
): Set<string> {
  const out = new Set<string>();
  if (!deployedRegions?.length || !catalogItems?.length) return out;
  for (const item of catalogItems) {
    const g = regionItemRegionalGroup(item);
    if (!g) continue;
    const opt: RegionOption = {
      key: item.targetRegion,
      value: item.targetRegion,
      regionalGroup: g,
    };
    if (isRegionAlreadyDeployed(opt, deployedRegions)) {
      out.add(normKey(g));
    }
  }
  return out;
}

/**
 * True when this region is not itself deployed but shares a regionalGroup with some
 * deployed region (possibly another provider).
 */
export function isRegionGreyedOutByOccupiedRegionalGroup(
  option: RegionOption,
  occupiedRegionalGroupKeys: Set<string>,
  deployedRegions: string[] | undefined
): boolean {
  const g = option.regionalGroup?.trim();
  if (!g || occupiedRegionalGroupKeys.size === 0) return false;
  if (isRegionAlreadyDeployed(option, deployedRegions)) return false;
  return occupiedRegionalGroupKeys.has(normKey(g));
}

/**
 * True when the user can still add a deployment in the regional flow: at least one
 * AWS or Azure region exists that is neither deployed nor blocked by regionalGroup occupancy.
 * Empty catalog returns true so the Add button stays available until regions load (or if the API fails).
 */
export function hasAvailableRegionalDeploymentSlot(
  deployedRegions: string[] | undefined,
  catalogItems: RegionItemFromApi[]
): boolean {
  if (!catalogItems?.length) {
    return true;
  }
  const occupied = buildOccupiedRegionalGroupKeys(deployedRegions, catalogItems);
  const providers: Array<"AWS" | "AZURE"> = ["AWS", "AZURE"];
  for (const provider of providers) {
    const filtered = filterRegionsByProvider(catalogItems, provider);
    if (!filtered.length) {
      continue;
    }
    const groups = regionItemsToRegionGroups(filtered);
    for (const group of groups) {
      for (const r of group.regions) {
        if (isRegionAlreadyDeployed(r, deployedRegions)) {
          continue;
        }
        if (
          isRegionGreyedOutByOccupiedRegionalGroup(
            r,
            occupied,
            deployedRegions
          )
        ) {
          continue;
        }
        return true;
      }
    }
  }
  return false;
}

/**
 * @deprecated Prefer {@link buildOccupiedRegionalGroupKeys} + {@link isRegionGreyedOutByOccupiedRegionalGroup}
 * for cross-provider grouping; this only considers siblings in {@code allOptions}.
 */
export function isRegionGreyedOutForRegionalGroup(
  option: RegionOption,
  allOptions: RegionOption[],
  deployedRegions: string[] | undefined
): boolean {
  const catalog: RegionItemFromApi[] = allOptions.map((o) => ({
    geography: "",
    targetRegion: o.value,
    regionalGroup: o.regionalGroup,
  }));
  return isRegionGreyedOutByOccupiedRegionalGroup(
    option,
    buildOccupiedRegionalGroupKeys(deployedRegions, catalog),
    deployedRegions
  );
}

export interface RegionGroup {
  label: string;
  regions: RegionOption[];
}

/** Shape of each item from GET /v2/apis/regions */
export interface RegionItemFromApi {
  geography: string;
  targetRegion: string;
  /** API may send target cloud for the deployment (preferred). */
  targetProvider?: string;
  /** Legacy/alternate field name for the same concept. */
  provider?: string;
  regionalGroup?: string | null;
  /** Host lookup key (e.g. us-e); distinct from regionalGroup display name (e.g. US East) */
  basePathIdentifier?: string | null;
}

function regionItemRegionalGroup(item: RegionItemFromApi): string | undefined {
  const v = item.regionalGroup;
  if (v == null || String(v).trim() === "") return undefined;
  return String(v).trim();
}

/** Filter regions to only those for the given provider (AWS | AZURE). Items without a recognizable provider are excluded when a provider is specified. */
/** User-facing label for deployment/API provider strings (e.g. AWS, Azure). */
export function formatTargetCloudProviderLabel(raw?: string | null): string | null {
  if (raw == null || String(raw).trim() === "") return null;
  const u = String(raw).trim().toUpperCase();
  if (u === "AWS") return "AWS";
  if (u === "AZURE") return "Azure";
  return String(raw).trim();
}

export function filterRegionsByProvider(
  items: RegionItemFromApi[],
  provider: "AWS" | "AZURE"
): RegionItemFromApi[] {
  const upper = provider.toUpperCase();
  return items.filter((item) => {
    const p = item.targetProvider ?? item.provider;
    return p && p.toUpperCase() === upper;
  });
}

/** Build region groups from API response: geography = group title, targetRegion = option value/label */
export function regionItemsToRegionGroups(items: RegionItemFromApi[]): RegionGroup[] {
  const byGeography = new Map<string, RegionOption[]>();
  for (const item of items) {
    const bpi = item.basePathIdentifier;
    const opt: RegionOption = {
      key: item.targetRegion,
      value: item.targetRegion,
      regionalGroup: regionItemRegionalGroup(item),
      basePathIdentifier:
        bpi != null && String(bpi).trim() !== "" ? String(bpi).trim() : undefined,
    };
    const list = byGeography.get(item.geography) ?? [];
    list.push(opt);
    byGeography.set(item.geography, list);
  }
  return Array.from(byGeography.entries()).map(([label, regions]) => ({ label, regions }));
}

export const REGION_GROUPS: RegionGroup[] = [
  {
    label: "North America",
    regions: [
      { key: "US East (N. Virginia)", value: "us-east-1" },
      { key: "US East (Ohio)", value: "us-east-2" },
      { key: "US West (N. California)", value: "us-west-1" },
      { key: "US West (Oregon)", value: "us-west-2" },
    ],
  },
  {
    label: "South America",
    regions: [
      { key: "São Paulo", value: "sa-east-1" },
    ],
  },
  {
    label: "Europe",
    regions: [
      { key: "EU West (Ireland)", value: "eu-west" },
      { key: "EU North (Stockholm)", value: "eu-north-1" },
    ],
  },
  {
    label: "Middle east & Africa",
    regions: [
      { key: "Middle East (Bahrain)", value: "me-south-1" },
      { key: "Africa (Cape Town)", value: "af-south-1" },
    ],
  },
  {
    label: "Asia Pacific",
    regions: [
      { key: "Asia Pacific (Singapore)", value: "ap-southeast-1" },
      { key: "Asia Pacific (Tokyo)", value: "ap-northeast-1" },
      { key: "Asia Pacific (Sydney)", value: "ap-southeast-2" },
    ],
  },
  {
    label: "Australia & New Zealand",
    regions: [
      { key: "Australia (Sydney)", value: "ap-southeast-2" },
      { key: "Australia (Melbourne)", value: "ap-southeast-4" },
    ],
  },
  {
    label: "Other",
    regions: [
      { key: "Global", value: "global" },
      { key: "Europe", value: "europe" },
      { key: "Asia Pacific", value: "asia-pacific" },
      { key: "Australia", value: "australia" },
      { key: "United Kingdom", value: "uk" },
    ],
  },
];

/** Flatten all region values for availability checks */
export function getAllRegionValues(): string[] {
  return REGION_GROUPS.flatMap((g) => g.regions.map((r) => r.value));
}

/** Get region display name by value (for deployV2 targetRegion when not global) */
export function getRegionDisplayName(value: string): string | undefined {
  for (const group of REGION_GROUPS) {
    const found = group.regions.find((r) => r.value === value);
    if (found) return found.key;
  }
  return undefined;
}

const OTHER_GROUP_LABEL = "Other";

/** Returns true if the given region value belongs to the "Other" group */
export function isRegionInOtherGroup(regionValue: string): boolean {
  const otherGroup = REGION_GROUPS.find((g) => g.label === OTHER_GROUP_LABEL);
  return otherGroup?.regions.some((r) => r.value === regionValue) ?? false;
}

/**
 * Map from target region (display name) to assigned gateway label.
 * Key = selected region label, Value = gateway description for "Assigned gateway" alert.
 */
const ASSIGNED_GATEWAY_BY_REGION: Record<string, string> = {
  // AWS - North America
  "US East (Ohio)":
    'Multi regional gateway - "US-West-2 (Primary), US-East, Southeast Asia, North Europe"',
  "US East (N. Virginia)":
    'Multi regional gateway - "US-West-2 (Primary), US-East, Southeast Asia, North Europe"',
  "US West (Oregon)":
    'Multi regional gateway - "US-West-2 (Primary), US-East, Southeast Asia, North Europe"',
  "US West (N. California)":
    'Multi regional gateway - "US-West-2 (Primary), US-East, Southeast Asia, North Europe"',

  // AWS - Europe
  "Europe (Stockholm)": "North Europe",

  // AWS - Asia Pacific
  "Asia Pacific (Singapore)":
    'Multi regional gateway - "Southeast Asia (Primary) Central India"',
  "Asia Pacific (Sydney)":
    'Multi regional gateway - "Australia East (Primary) Australia Southeast"',
  "Asia Pacific (Jakarta)":
    'Multi regional gateway - "Southeast Asia (Primary) Central India"',
  "Asia Pacific (Melbourne)":
    'Multi regional gateway - "Australia East (Primary) Australia Southeast"',
  "Asia Pacific (Malaysia)":
    'Multi regional gateway - "Southeast Asia (Primary) Central India"',
  "Asia Pacific (New Zealand)":
    'Multi regional gateway - "Southeast Asia (Primary) Central India"',
  "Asia Pacific (Thailand)":
    'Multi regional gateway - "Southeast Asia (Primary) Central India"',

  // Azure - Americas
  "East US":
    'Multi regional gateway - "US-West-2 (Primary), US-East, Southeast Asia, North Europe"',
  "East US 2":
    'Multi regional gateway - "US-West-2 (Primary), US-East, Southeast Asia, North Europe"',
  "South Central US":
    'Multi regional gateway - "US-West-2 (Primary), US-East, Southeast Asia, North Europe"',
  "West US":
    'Multi regional gateway - "US-West-2 (Primary), US-East, Southeast Asia, North Europe"',
  "West US 2":
    'Multi regional gateway - "US-West-2 (Primary), US-East, Southeast Asia, North Europe"',
  "West US 3":
    'Multi regional gateway - "US-West-2 (Primary), US-East, Southeast Asia, North Europe"',
  "West Central US":
    'Multi regional gateway - "US-West-2 (Primary), US-East, Southeast Asia, North Europe"',

  // Azure - Europe
  "North Europe": "North Europe",

  // Azure - Asia Pacific
  "Australia East":
    'Multi regional gateway - "Australia East (Primary) Australia Southeast"',
  "Australia Southeast":
    'Multi regional gateway - "Australia East (Primary) Australia Southeast"',
  "Central India":
    'Multi regional gateway - "Southeast Asia (Primary) Central India"',

  // Existing generic options
  Global:
    'Multi regional gateway - "US-West-2 (Primary), US-East, Southeast Asia, North Europe"',
  Europe: "North Europe",
};

const DEFAULT_ASSIGNED_GATEWAY =
  'Multi regional gateway - "US-West-2 (Primary), US-East, Southeast Asia, North Europe"';

/** Get the assigned gateway label for the given target region display name (region key). */
export function getAssignedGatewayLabel(regionDisplayName: string): string {
  return ASSIGNED_GATEWAY_BY_REGION[regionDisplayName] ?? DEFAULT_ASSIGNED_GATEWAY;
}

/**
 * Short gateway name for copy (e.g. Global, EU, US, AP, AU) from API gateway region keys
 * (edit/details when basePathIdentifier is unavailable).
 */
export function getGatewayBrandNameFromGatewayRegionKey(
  gatewayRegion?: string | null
): string {
  if (gatewayRegion == null || String(gatewayRegion).trim() === "") return "—";
  const k = String(gatewayRegion).trim().toLowerCase();
  if (k === "global") return "Global";
  if (k.startsWith("us-")) return "US";
  if (k.startsWith("eu-") || k === "europe" || k === "uk") return "EU";
  if (
    k === "australia" ||
    k === "au" ||
    k === "ap-southeast-2" ||
    k === "ap-southeast-4"
  ) {
    return "AU";
  }
  if (k.startsWith("ap-") || k === "asia-pacific" || k === "ap") return "AP";
  if (k === "me-south-1" || k === "af-south-1") return "AP";
  return "US";
}
